import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { accountsAPI } from '../lib/api';
import { formatCurrency, cn } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2, Edit2, RefreshCw, Wallet, Building2, Banknote, CreditCard, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const ACCOUNT_TYPES = [
  { value: 'bank', label: 'Banco', icon: Building2 },
  { value: 'cash', label: 'Efectivo', icon: Banknote },
  { value: 'credit_card', label: 'Tarjeta de Crédito', icon: CreditCard },
  { value: 'investment', label: 'Inversión', icon: TrendingUp },
];

const COLORS = [
  '#064E3B', '#D97706', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#EF4444', '#F59E0B', '#06B6D4', '#6B7280'
];

const Accounts = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);
  
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAcc, setEditingAcc] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    type: 'bank',
    balance: '0',
    currency: 'EUR',
    icon: 'building-2',
    color: '#064E3B'
  });

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await accountsAPI.getAll();
      setAccounts(response.data);
    } catch (error) {
      toast.error('Error al cargar cuentas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      const payload = {
        ...form,
        balance: parseFloat(form.balance) || 0
      };

      if (editingAcc) {
        await accountsAPI.update(editingAcc.id, payload);
        toast.success(t('accountUpdated'));
      } else {
        await accountsAPI.create(payload);
        toast.success(t('accountCreated'));
      }
      
      setDialogOpen(false);
      setEditingAcc(null);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta cuenta?')) return;
    
    try {
      await accountsAPI.delete(id);
      toast.success(t('accountDeleted'));
      fetchAccounts();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (acc) => {
    setEditingAcc(acc);
    setForm({
      name: acc.name,
      type: acc.type,
      balance: acc.balance.toString(),
      currency: acc.currency,
      icon: acc.icon,
      color: acc.color
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      type: 'bank',
      balance: '0',
      currency: user?.currency || 'EUR',
      icon: 'building-2',
      color: '#064E3B'
    });
  };

  const openNewDialog = () => {
    setEditingAcc(null);
    resetForm();
    setDialogOpen(true);
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  const getTypeIcon = (type) => {
    const typeObj = ACCOUNT_TYPES.find(t => t.value === type);
    return typeObj?.icon || Wallet;
  };

  const getTypeLabel = (type) => {
    const typeObj = ACCOUNT_TYPES.find(t => t.value === type);
    return typeObj?.label || type;
  };

  return (
    <div className="space-y-6" data-testid="accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
            {t('accounts')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Balance total: <span className="font-mono font-medium text-slate-900">
              {formatCurrency(totalBalance, user?.currency)}
            </span>
          </p>
        </div>
        <Button 
          onClick={openNewDialog}
          className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
          data-testid="new-account-btn"
        >
          <Plus size={16} className="mr-2" />
          {t('newAccount')}
        </Button>
      </div>

      {/* Accounts Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-900" />
        </div>
      ) : accounts.length === 0 ? (
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Wallet size={32} className="mb-2" />
            <p>No hay cuentas</p>
            <Button variant="link" onClick={openNewDialog} className="text-emerald-900">
              Crear primera cuenta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map(acc => {
            const Icon = getTypeIcon(acc.type);
            return (
              <Card 
                key={acc.id}
                className="border-slate-200 rounded-sm shadow-none hover:border-emerald-900/30 transition-colors"
                data-testid={`account-${acc.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: `${acc.color}15` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: acc.color }} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{acc.name}</p>
                        <p className="text-xs text-slate-400">{getTypeLabel(acc.type)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleEdit(acc)}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-900"
                        data-testid={`edit-acc-${acc.id}`}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(acc.id)}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                        data-testid={`delete-acc-${acc.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                  <p className={cn(
                    "font-mono text-2xl font-medium",
                    acc.balance >= 0 ? "text-slate-900" : "text-red-500"
                  )}>
                    {formatCurrency(acc.balance, acc.currency)}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Account Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingAcc ? t('editAccount') : t('newAccount')}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('accountName')} *
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                className="rounded-sm"
                placeholder="Nombre de la cuenta"
                required
                data-testid="account-name-input"
              />
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('accountType')}
              </Label>
              <Select value={form.type} onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="account-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Balance */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('balance')}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {user?.currency === 'EUR' ? '€' : '$'}
                </span>
                <Input
                  type="number"
                  step="0.01"
                  value={form.balance}
                  onChange={(e) => setForm(prev => ({ ...prev, balance: e.target.value }))}
                  className="pl-8 font-mono rounded-sm"
                  data-testid="account-balance-input"
                />
              </div>
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('color')}
              </Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, color }))}
                    className={cn(
                      "w-8 h-8 rounded-sm transition-transform",
                      form.color === color && "ring-2 ring-offset-2 ring-slate-900 scale-110"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
              >
                {t('cancel')}
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
                data-testid="save-account-btn"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Accounts;
